import { PlusIcon } from '@heroicons/react/24/outline';
import { CustomerOrder } from '../services/customerService';
import { Badge } from '../../../components';
import { formatDate, formatCurrency } from '../../../lib/utils';
import { useAuthStore } from '../../../stores';

interface RecentOrdersProps {
  orders: CustomerOrder[];
  onCreateOrder?: () => void;
  onViewOrder?: (orderId: string) => void;
}

export default function RecentOrders({ orders, onCreateOrder, onViewOrder }: RecentOrdersProps) {
  const currency = useAuthStore((s) => s.currency);
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'shipped':
      case 'confirmed':
        return 'info';
      case 'processing':
      case 'pending':
      case 'on_hold':
        return 'warning';
      case 'cancelled':
      case 'refunded':
      case 'partially_refunded':
      case 'failed':
        return 'danger';
      default:
        return 'default';
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-ink">Recent Orders</h4>
        {onCreateOrder && (
          <button
            onClick={onCreateOrder}
            className="flex items-center gap-1 rounded-pill px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Create Order
          </button>
        )}
      </div>

      <div className="space-y-3">
        {orders.length === 0 && (
          <p className="py-4 text-center text-xs text-ink-3">No orders found</p>
        )}
        {orders.slice(0, 5).map((order) => (
          <div
            key={order.id}
            className="cursor-pointer rounded-card border border-line bg-surface p-3 hover:bg-black/[0.03]"
            onClick={() => onViewOrder?.(order.id)}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-ink">
                  #{order.orderNumber}
                </p>
                <p className="mt-0.5 text-xs text-ink-3">
                  {formatDate(order.createdAt)}
                </p>
              </div>
              <Badge variant={getStatusVariant(order.status)} size="sm">
                {order.status}
              </Badge>
            </div>

            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-ink-2">
                {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
              </span>
              <span className="text-sm font-semibold text-ink">
                {formatCurrency(order.total, currency)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
